import { createInstance, dateReviver } from '@/api';
import {
    CreateOperationEntityDto,
    OperationEntityClient,
    OperationEntityDetailDto,
    UpdateOperationEntityDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useDeliveryProcessDefinitionEditStore } from '../delivery-process-definition/delivery-process-definition-edit.store';
import { useDeliveryTransformationStore } from '../delivery-transformation/delivery-transformation.store';
import { useOperationEntityDataMappingStore } from '../operation-entity-data-mapping/operation-entity-data-mapping.store';

export interface OperationEntityStoreState {
    items?: { key: string; value: OperationEntityDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new OperationEntityClient('', createInstance(), dateReviver);

export const useOperationEntityStore = defineStore('configuration-operation-entity-store', {
    state: (): OperationEntityStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): OperationEntityDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        },
        bySpecification: (state) => {
            return (specificationId: number): OperationEntityDetailDto[] => {
                const key = `specification-${specificationId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async updateOperationEntityUI(items: OperationEntityDetailDto[]) {
            items?.forEach((item) => {
                useDeliveryTransformationStore().updateOperationEntityUI(item);
                useOperationEntityDataMappingStore().updateOperationEntityUI(item);
            });
        },
        async getByDefinition(definitionId: number, reloading: boolean = false) {
            const key = `definition-${definitionId}`;
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateOperationEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('operationentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async getBySpecification(specificationId: number, reloading: boolean = false) {
            const key = `specification-${specificationId}`;
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                const response = await client.getBySpecification(specificationId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateOperationEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('operationentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(specificationId: number, dto: CreateOperationEntityDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                this.getBySpecification(specificationId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('operationentity.detail.error.create.title'), error);
            } finally {
                this.reloading = false;
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(specificationId: number, entityId: number, dto: UpdateOperationEntityDto) {
            let hasError = false;
            this.reloading = true;
            try {
                await client.update(entityId, dto);
                useAlertStore().setSuccess(i18n.global.t('operationentity.detail.success.update.title'));
                this.getBySpecification(specificationId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('operationentity.detail.error.update.title'), error);
            } finally {
                this.reloading = false;
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(specificationId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('operationentity.detail.success.delete.title'));
                await this.getBySpecification(specificationId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('operationentity.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(specificationId: number) {
            const key = `specification-${specificationId}`;
            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
